import React, { useState, useEffect, useCallback, useMemo } from 'react';
import OrderService from '../services/OrderService';
import { SupplierOrder, ReceiveItem } from '../types';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import { HttpError } from '../../../core/services/HttpService';
import { StatusBadge } from './OrderStatusBadge';
import { SuccessOverlay, SuccessType } from './OrderSuccessOverlay';
import styles from './OrdersPage.module.css';

const COLORS = {
  navy: '#0E1333',
  green: '#10B981',
  greenLight: '#D1FAE5',
  muted: '#64748b',
  text: '#334155',
  red: '#EF4444',
};

interface ReceiveState {
  [itemId: number]: boolean;
}

export function ReceiveView({ order, onReceive }: {
  order: SupplierOrder;
  onReceive: () => void;
}): React.JSX.Element {
  const items = order.items || [];
  const [receiveState, setReceiveState] = useState<ReceiveState>(() => {
    const initial: ReceiveState = {};
    items.forEach((item) => { initial[item.id] = false; });
    return initial;
  });
  const [successType, setSuccessType] = useState<SuccessType>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const initial: ReceiveState = {};
    (order.items || []).forEach((item) => { initial[item.id] = false; });
    setReceiveState(initial);
  }, [order.id]);

  const receivedCount = useMemo(() =>
    Object.values(receiveState).filter(Boolean).length,
    [receiveState]
  );

  const allSelected = receivedCount === items.length && items.length > 0;

  const toggleItem = (itemId: number) => {
    setReceiveState((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const toggleAll = () => {
    const newState: ReceiveState = {};
    const targetValue = !allSelected;
    items.forEach((item) => { newState[item.id] = targetValue; });
    setReceiveState(newState);
  };

  const handleConfirmDelivery = useCallback(async () => {
    setConfirming(true);
    const receiveItems: ReceiveItem[] = items.map((item) => ({
      order_item_id: item.id,
      received: receiveState[item.id] ?? false,
      quantity_received: receiveState[item.id] ? item.quantity_ordered : 0,
    }));
    try {
      await OrderService.receiveOrder(order.id, receiveItems);
      setSuccessType('received');
      setTimeout(() => {
        onReceive();
      }, 2000);
    } catch (err) {
      if (err instanceof HttpError) globalErrorHandler.handle(err);
      setConfirming(false);
    }
  }, [order.id, items, receiveState, onReceive]);

  return (
    <div className={styles.receiveViewContainer}>
      <SuccessOverlay type={successType} onDismiss={() => setSuccessType(null)} />

      <div className={styles.draftViewHeader}>
        <div className={styles.draftViewTitleRow}>
          <h3 className={styles.viewTitle}>{order.supplier_name}</h3>
          <StatusBadge label="Delivery arrived!" color={COLORS.green} bgColor={COLORS.greenLight} pulsing />
        </div>
        <div className={styles.receiveSubtitle}>Check all delivered items</div>
      </div>

      <div className={styles.scrollArea}>
        <div onClick={toggleAll} className={styles.selectAllRow}>
          <div
            className={styles.checkCircle}
            style={{ background: allSelected ? COLORS.green : '#E5E7EB' }}
          >
            {allSelected && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17L4 12" />
              </svg>
            )}
          </div>
          <span className={styles.selectAllLabel}>Select All</span>
        </div>

        {items.map((item) => {
          const isReceived = receiveState[item.id] ?? false;
          return (
            <div
              key={item.id}
              onClick={() => toggleItem(item.id)}
              className={styles.receiveItemCard}
            >
              <div
                className={styles.checkCircle}
                style={{ background: isReceived ? COLORS.green : '#FEE2E2', flexShrink: 0 }}
              >
                {isReceived ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17L4 12" />
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={COLORS.red} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18" />
                    <path d="M6 6L18 18" />
                  </svg>
                )}
              </div>
              <div className={styles.itemNameContainer}>
                <div className={styles.itemName} style={{ whiteSpace: 'normal' }}>{item.name}</div>
                <div className={styles.itemUnit}>{item.unit}</div>
              </div>
              <span className={styles.itemQuantityText}>{item.quantity_ordered}x</span>
            </div>
          );
        })}
      </div>

      <div className={styles.receiveFooter}>
        <div className={styles.receiveCount}>
          {receivedCount}/{items.length} items received
        </div>
        <button
          onClick={handleConfirmDelivery}
          disabled={confirming}
          className={styles.confirmDeliveryBtn}
          style={{ opacity: confirming ? 0.7 : 1 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17L4 12" />
          </svg>
          Confirm Delivery
        </button>
      </div>
    </div>
  );
}
